import React, { useCallback } from "react";
import { connect } from "react-redux";

import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import temporaryFields from "@app/assets/layouts/temporary-fields.png";
import Checkbox from "@app/component/input/Checkbox";
import { RootState } from "@app/store/rootReducer";

import { getEditedLayoutAllowAdd, isEditingChildLayout } from "../../store";
import { updateLayout } from "../../store/actions";

import styles from "./TemporaryFieldsCheckbox.module.scss";

interface Props {
  checked: boolean;
  isEditingChildLayout: boolean;
  updateLayout: typeof updateLayout;
}

const TemporaryFieldsCheckbox: React.FC<Props> = ({
  checked,
  isEditingChildLayout,
  updateLayout,
}) => {
  const intl = useIntl();

  const handleChange = useCallback(
    (value: boolean) => updateLayout({ addFields: value }),
    [updateLayout]
  );

  return (
    <div className={styles.container}>
      <div className={styles.inner}>
        <Checkbox checked={checked} disabled={isEditingChildLayout} onChange={handleChange}>
          {intl.formatMessage({
            id: "settings.formLayouts.modal.properties.temporaryFields.label",
            defaultMessage: "Permit users to temporarily add fields to form",
            description: "Permit users to temporarily add fields to form",
          })}
        </Checkbox>

        {isEditingChildLayout ? (
          <Tooltip
            placement="bottom"
            title={intl.formatMessage({
              id: "settings.formLayouts.modal.properties.temporaryFields.gotoPrimary",
              defaultMessage: "Go to primary layout to change this",
              description: "Go to primary layout to change this",
            })}
          >
            <FontAwesomeIcon icon={faInfoCircle} size="xs" />
          </Tooltip>
        ) : (
          <Popover
            content={
              <div>
                <div className={styles.title}>
                  {intl.formatMessage({
                    id: "settings.formLayouts.modal.properties.temporaryFields.title",
                    defaultMessage: "Temporary Fields",
                    description: "Permit users to temporarily add fields to form - title",
                  })}
                </div>

                <div className={styles.content}>
                  <div className={styles.description}>
                    {intl.formatMessage(
                      {
                        id: "settings.formLayouts.modal.properties.temporaryFields.text",
                        defaultMessage:
                          "Enabling this <b>gives your team the flexibility</b> to add any field* to their form for <i>only that instance</i>. Generally, it’s helpful to limit your layouts to what is truly necessary and allow your team to add more information as needed.",
                        description: "Permit users to temporarily add fields to form - text",
                      },
                      {
                        b: (text) => <strong>{text}</strong>,
                        i: (text) => <em>{text}</em>,
                      }
                    )}
                  </div>

                  <div className={styles.picture}>
                    <img alt="Temporary Fields" src={temporaryFields} />
                  </div>
                </div>

                <div className={styles.note}>
                  {intl.formatMessage({
                    id: "settings.formLayouts.modal.properties.temporaryFields.note",
                    defaultMessage: "*Any field they have permission to edit",
                    description: "Permit users to temporarily add fields to form - note",
                  })}
                </div>
              </div>
            }
            overlayClassName={styles.overlay}
            placement="bottomLeft"
            trigger={["hover"]}
          >
            <FontAwesomeIcon icon={faInfoCircle} size="xs" />
          </Popover>
        )}
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  checked: getEditedLayoutAllowAdd(state),
  isEditingChildLayout: isEditingChildLayout(state),
});

const mapDispatchToProps = {
  updateLayout,
};

export default connect(mapStateToProps, mapDispatchToProps)(TemporaryFieldsCheckbox);
